import { useEffect, useState } from 'react';
import { app, describeError, go } from '../state/app';
import { getCharacter } from '../api/endpoints';
import { draft, speculatePeek } from '../state/speculate';
import { poll, type PollHandle } from '../lib/poll';
import { mediaUrl } from '../lib/media';
import { TierDot, type DotTier } from '../components/TierDot';
import { DEBUG } from '../runtime';
import type { Candidate } from '../api/types';

/**
 * Screen 2. Four variants, always four — that fixed list is the only reason
 * the starter tree is enumerable and therefore pre-bakeable. One tap silently
 * completes the character sheet (pose + world), so there is no questionnaire.
 */
export function Candidates() {
  const { entry, character } = app.use();
  // What the parent actually tapped or typed. `label` is the starter's name on
  // the guided path and the gate's cleaned-up description on the custom one.
  const chosenLabel = entry?.label?.trim();
  const [error, setError] = useState<string | null>(null);
  const [waited, setWaited] = useState(false);
  // Candidate indexes whose art 404'd — the same placeholder path as "still
  // baking", because a broken-image icon reads as a crash to a parent.
  const [brokenArt, setBrokenArt] = useState<Set<number>>(new Set());

  /// Which tiles have their picture actually ON SCREEN, as opposed to merely
  /// existing on the server. Gates the tap; see the note at the `shown` line.
  const [loadedArt, setLoadedArt] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!entry) {
      go('gallery');
      return;
    }
    let alive = true;
    let handle: PollHandle | null = null;

    // Memoized: a speculative draft fired while the parent was still reading
    // the gate result resolves to this exact promise. No second spend.
    draft(entry, false)
      .then((created) => {
        if (!alive) return;
        app.set({ characterId: created.characterId });

        if (created.candidates?.length) {
          // Reshaped backend: a catalog tile returns 201 with the pre-baked
          // candidates inline — no CandidatesFn invoke, no polling at all.
          app.set({
            character: {
              characterId: created.characterId,
              status: created.status ?? 'awaiting_selection',
              candidates: created.candidates,
            },
          });
          return;
        }

        handle = poll({
          fetch: () => getCharacter(created.characterId),
          onValue: (value) => app.set({ character: value }),
          done: (value) => value.status === 'awaiting_selection' || value.status === 'ready',
          onError: (err) => setError(describeError(err)),
          onTimeout: () => setWaited(true),
          intervalMs: 1000,
          timeoutMs: 3 * 60 * 1000,
        });
      })
      .catch((err) => alive && setError(describeError(err)));

    return () => {
      alive = false;
      handle?.cancel();
    };
  }, [entry]);

  const candidates = character?.candidates ?? [];
  const slots: (Candidate | null)[] = [0, 1, 2, 3].map((i) => candidates[i] ?? null);
  const hero = entry?.portraitUrl ?? firstImage(candidates);

  const choose = (index: number) => {
    app.set({ chosenIndex: index });
    // THE NAMING SCREEN IS THE ONLY DEAD TIME IN THE WIZARD, so the free peek
    // starts here rather than when the parent arrives at the summary and
    // watches "Sketching the scenes…" for several seconds. Nothing is awaited:
    // the tap has to land on the next screen immediately, and a peek that
    // never starts only means the summary waits the way it used to.
    //
    // It is capped at one per character inside speculatePeek, which is what
    // stops "tile 2, back, tile 3, back, tile 2" from billing three preview
    // tokens for one moment of indecision.
    void speculatePeek(app.get().characterId ?? '', index);
    go('name');
  };

  return (
    <>
      <div className="pane-img">
        {hero ? (
          <img src={hero} alt="" />
        ) : (
          <div className="placeholder pulse">
            {entry?.label ?? 'your character'}
            <br />
            <span style={{ fontSize: 12 }}>drawing…</span>
          </div>
        )}
      </div>
      <div className="pane-q">
        <button className="backbtn" onClick={() => go('gallery')}>
          ‹ back
        </button>
        {/* Name the creature, and stay neutral: we never ask the child's gender,
            and the old copy assumed one. It also gave no confirmation
            that the right tile was tapped, which is the one thing a parent
            wants to see after choosing. */}
        <h1>{chosenLabel ? `${chosenLabel} — let them pick` : 'Hand it over — let them pick'}</h1>
        <p className="sub">
          One tap sets the whole world: pose and place, no questions.
        </p>

        {error && <div className="err">{error}</div>}
        {waited && (
          // WHAT A PARENT CAN DO ABOUT IT, in their own language. This used to
          // read "With pipeline/providers/images.py still raising
          // NotImplementedError this is the expected scaffold state — pick a
          // placeholder and the rest of the flow still works", which named a
          // file the parent cannot open and then advised the one action that
          // dead-ends: a placeholder is not a candidate, and selecting one
          // answers "candidateIndex 0 is not ready" two screens later.
          <div className="err">
            We couldn’t draw them this time. Nothing has been charged — go back
            and pick again, or choose one of the ready-made characters.
          </div>
        )}

        <div className="cards">
          {slots.map((candidate, i) => {
            const url = brokenArt.has(i) ? null : imageFor(candidate);
            // A SLOT WITH NOTHING IN IT IS NOT A CHOICE. All four tiles were
            // live from the first paint, so a tap on a still-drawing placeholder
            // — and the heading invites the CHILD to do the tapping — carried an
            // index the server has no candidate for, through the naming screen,
            // to a raw "candidateIndex 0 is not ready" on the button that was
            // meant to be the happy ending. The tile keeps its drawing animation
            // and simply does not answer until there is something to pick.
            //
            // AND "THE SERVER HAS IT" IS NOT "THE CHILD CAN SEE IT". `pending`
            // going false only means the bytes exist in the container; the
            // browser has not fetched them yet, and on a fresh character that
            // download is the slowest part of the screen. So the tile used to
            // go live over a blank square, for as long as the image took — a
            // window of seconds, during which the heading is actively inviting
            // a toddler to tap, and every tile looks identical because none of
            // them has drawn yet. Whatever they picked would be a picture they
            // had never seen.
            //
            // `shown` is the onLoad below. It is the only signal that means
            // what the parent means by "ready".
            //
            // BROKEN ART STAYS SELECTABLE, deliberately. If the fetch fails the
            // candidate is still perfectly good on the server, and the pose is
            // recorded by index rather than by the picture — so refusing the
            // tap would strand a parent whose one flaky download was the tile
            // they wanted, with no way forward but starting the wizard again.
            // The dead-end-with-no-controls failure is the one this screen has
            // already been bitten by once.
            const shown = loadedArt.has(i) || brokenArt.has(i);
            const ready = Boolean(candidate) && !candidate?.pending && shown;
            return (
              <button
                className="card"
                key={i}
                disabled={!ready}
                /* Shows no text — the child picks the picture — but still
                   needs a name for screen readers and for tests. */
                aria-label={ready ? label(candidate, i) : 'still drawing'}
                onClick={() => choose(i)}
              >
                {DEBUG && (
                  <TierDot tier={brokenArt.has(i) ? 'failed' : dotFor(candidate)} />
                )}
                {url ? (
                  <img
                    src={url}
                    alt={candidate?.slug ?? ''}
                    /* A CACHED IMAGE MAY BE COMPLETE BEFORE REACT ATTACHES
                       onLoad, and then the event never fires and the tile stays
                       shut for ever. That is not hypothetical here: going back
                       from the naming screen and returning re-mounts this list
                       against a warm HTTP cache, which is the single commonest
                       way to arrive at it twice. The ref runs on mount and asks
                       the element directly. */
                    ref={(el) => {
                      if (el?.complete && el.naturalWidth > 0) {
                        setLoadedArt((prev) =>
                          prev.has(i) ? prev : new Set(prev).add(i));
                      }
                    }}
                    onLoad={() => setLoadedArt((prev) => new Set(prev).add(i))}
                    onError={() => setBrokenArt((prev) => new Set(prev).add(i))}
                  />
                ) : (
                  <div className="empty drawing">
                    <span className="drawing-dots">✦</span>
                  </div>
                )}
                {/* No caption. It read "gentle · cottage-garden" — our
                    personality/world slugs — and "option 3" before the art
                    landed. The child picks the picture; an accessible name
                    carries the same information for screen readers. */}
              </button>
            );
          })}
        </div>

        {/* The cache-tier legend lived here: "on device · catalog / shared
            pool · made fresh · baking". That is our plumbing, and a parent
            reading it learns only that their child's character came out of a
            cache. It survives behind the dev panel for us. */}
      </div>
    </>
  );
}

function imageFor(candidate: Candidate | null): string | null {
  if (!candidate) return null;
  return candidate.url ?? mediaUrl(candidate.key ?? candidate.storageKey);
}

function firstImage(candidates: Candidate[]): string | null {
  for (const candidate of candidates) {
    const url = imageFor(candidate);
    if (url) return url;
  }
  return null;
}

function dotFor(candidate: Candidate | null): DotTier {
  if (!candidate) return 'baking';
  if (candidate.pending) return 'baking';
  if (candidate.tier) return candidate.tier;
  return 'unknown';
}

/** An accessible name for a tile that deliberately shows no text. */
function label(candidate: Candidate | null, index: number): string {
  const sheet = candidate?.sheet;
  const parts = [sheet?.personality, sheet?.world]
    .filter(Boolean)
    .map((part) => String(part).replace(/-/g, ' '));
  return parts.length ? parts.join(', ') : `picture ${index + 1}`;
}
